import React, { useEffect, useState, useRef } from "react";
import Typeahead from "$components/form-inputs/typeahead";
import api from "$services/api";
import { initiativesApi } from "../api";
import {
    InitiativeDetailsStore,
    addCollaboratorToStore,
    removeCollaboratorFromStore,
} from "$stores/initiative-details";
import { closeModal } from "$stores/modal";
import "./CollaboratorModal.scss";

const CollaboratorModal = ({ initiativeId, isOpen }) => {
    const { initiative, collaborators } = InitiativeDetailsStore.useState((s) => s);
    const [suggestions, setSuggestions] = useState([]);
    const [searchOptions, setSearchOptions] = useState([]);
    const [typeaheadKey, setTypeaheadKey] = useState(0);

    const [searching, setSearching] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null);
    const [reassignUserId, setReassignUserId] = useState("");
    const [openFollowUps, setOpenFollowUps] = useState([]);
    const [addError, setAddError] = useState(null);
    const [removeError, setRemoveError] = useState(null);
    const searchController = useRef(null);
    const searchDebounce = useRef(null);

    // Reset typeahead and re-fetch suggestions whenever the modal opens.
    // The modal component is reused across different initiative opens (not remounted),
    // so isOpen transitions are the correct trigger — not component mount.
    useEffect(() => {
        if (!isOpen) return;

        setTypeaheadKey((k) => k + 1);
        setSearchOptions([]);
        setSuggestions([]);

        const controller = new AbortController();
        initiativesApi.getMyCollaborators(controller.signal)
            .then((all) => {
                const collaboratorIds = new Set(collaborators.map((c) => c.userId));
                setSuggestions((all || []).filter((p) => !collaboratorIds.has(p.userId)));
            })
            .catch(() => {});
        return () => controller.abort();
    }, [isOpen]);

    const handleSearch = (query) => {
        if (!query) {
            setSearchOptions([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => {
            if (searchController.current) searchController.current.abort();
            const controller = new AbortController();
            searchController.current = controller;
            api.get(`/api/employees/sharable?query=${encodeURIComponent(query)}`, null, { signal: controller.signal })
                .then((data) => {
                    const employees = data?.employees || [];
                    const collaboratorIds = new Set(collaborators.map((c) => c.userId));
                    setSearchOptions(
                        employees
                            .filter((p) => !collaboratorIds.has(p.userId))
                            .map((p) => ({ value: p.userId, text: p.fullName }))
                    );
                    setSearching(false);
                })
                .catch(() => setSearching(false));
        }, 250);
    };

    const handleTypeaheadChange = async (_name, newValues) => {
        if (!newValues || newValues.length === 0) return;
        const added = newValues[0];
        setTypeaheadKey((k) => k + 1); // remount to clear selection
        setAddError(null);
        try {
            await initiativesApi.addCollaborator(initiativeId, added.value);
            addCollaboratorToStore({ userId: added.value, userName: added.text, addedDate: new Date().toISOString() });
            setSearchOptions((prev) => prev.filter((p) => p.value !== added.value));
            setSuggestions((prev) => prev.filter((p) => p.userId !== added.value));
        } catch {
            setAddError("Unable to add collaborator. Please try again.");
        }
    };

    const addFromSuggestions = async (person) => {
        setAddError(null);
        try {
            await initiativesApi.addCollaborator(initiativeId, person.userId);
            addCollaboratorToStore({ userId: person.userId, userName: person.userName, addedDate: new Date().toISOString() });
            setSuggestions((prev) => prev.filter((p) => p.userId !== person.userId));
        } catch {
            setAddError("Unable to add collaborator. Please try again.");
        }
    };

    const initiateRemove = async (collaborator) => {
        setRemoveError(null);
        try {
            const followUps = await initiativesApi.getFollowUps(initiativeId);
            const open = (followUps || []).filter(
                (f) => f.status === "open" && f.assignedToUserId === collaborator.userId
            );
            if (open.length > 0) {
                setOpenFollowUps(open);
                setReassignTarget(collaborator);
                setReassignUserId("");
            } else {
                await doRemove(collaborator.userId);
            }
        } catch {
            setRemoveError("Unable to remove collaborator. Please try again.");
        }
    };

    const doRemove = async (userId) => {
        await initiativesApi.removeCollaborator(initiativeId, userId);
        removeCollaboratorFromStore(userId);
        setReassignTarget(null);
        setOpenFollowUps([]);
        setReassignUserId("");
    };

    const confirmReassign = async () => {
        if (!reassignUserId) return;
        for (const fu of openFollowUps) {
            await initiativesApi.updateFollowUp(initiativeId, fu.id, {
                status: fu.status,
                commitmentText: fu.commitmentText,
                dueDate: fu.dueDate || null,
                clearDueDate: false,
                assignedToUserId: reassignUserId,
                clearAssignedUser: false,
            });
        }
        await doRemove(reassignTarget.userId);
    };

    const remainingCollaborators = collaborators.filter(
        (c) => c.userId !== reassignTarget?.userId
    );

    const [suggestionsOpen, setSuggestionsOpen] = useState(false);

    return (
        <div className="collaborator-modal">
            <h2>Manage Team</h2>

            {addError && <p className="txt -error -small">{addError}</p>}
            {removeError && <p className="txt -error -small">{removeError}</p>}

            {/* Reassignment prompt */}
            {reassignTarget && (
                <div className="collaborator-modal__reassign">
                    <p>
                        <strong>{reassignTarget.userName}</strong> has {openFollowUps.length} open action item{openFollowUps.length !== 1 ? "s" : ""}. Reassign before removing:
                    </p>
                    <div className="form-field">
                        <select
                            value={reassignUserId}
                            onChange={(e) => setReassignUserId(e.target.value)}
                        >
                            <option value="">Select new assignee...</option>
                            {remainingCollaborators.map((c) => (
                                <option key={c.userId} value={c.userId}>{c.userName}</option>
                            ))}
                        </select>
                    </div>
                    <div className="button-group">
                        <button
                            className="button -primary -small"
                            disabled={!reassignUserId}
                            onClick={confirmReassign}
                        >
                            Reassign &amp; Remove
                        </button>
                        <button
                            className="button -secondary -small"
                            onClick={() => { setReassignTarget(null); setOpenFollowUps([]); setReassignUserId(""); }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Section 1: Search by name */}
            <div className="collaborator-modal__section">
                <h4>Add by name</h4>
                <Typeahead
                    key={typeaheadKey}
                    name="collab-search"
                    cssClass="-full"
                    options={searchOptions}
                    value={[]}
                    apiLoading={searching}
                    optionLookup={handleSearch}
                    showSelectAndDeselectAll={false}
                    updateOnChange={handleTypeaheadChange}
                    placeholder="Search by name..."
                />
            </div>

            {/* Section 2: Quick-add from other initiatives */}
            {suggestions.length > 0 && (
                <>
                    <hr className="collaborator-modal__divider" />
                    <div className="collaborator-modal__section">
                        <button
                            className="collaborator-modal__suggestions-toggle"
                            onClick={() => setSuggestionsOpen((o) => !o)}
                        >
                            <span>People from your other initiatives ({suggestions.length})</span>
                            <span className="collaborator-modal__suggestions-arrow">{suggestionsOpen ? "▲" : "▼"}</span>
                        </button>
                        {suggestionsOpen && (
                            <ul className="collaborator-modal__list">
                                {suggestions.map((p) => (
                                    <li key={p.userId} className="collaborator-modal__person">
                                        <span className="collaborator-modal__person-name">{p.userName}</span>
                                        <button className="button -secondary -small" onClick={() => addFromSuggestions(p)}>Add</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {/* Section 3: Current team */}
            <hr className="collaborator-modal__divider" />
            <div className="collaborator-modal__section">
                <h4>Current team</h4>
                <ul className="collaborator-modal__list">
                    {collaborators.map((c) => {
                        const isOwner = c.userId === initiative?.ownerId;
                        return (
                            <li key={c.userId} className="collaborator-modal__person">
                                <span className="collaborator-modal__person-name">{c.userName}</span>
                                {isOwner ? (
                                    <span className="collaborator-modal__badge">Owner</span>
                                ) : (
                                    <button
                                        className="button -text -small"
                                        onClick={() => initiateRemove(c)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="button-group -right">
                <button className="button -primary -small" onClick={closeModal}>Done</button>
            </div>
        </div>
    );
};

export default CollaboratorModal;
